import {
  createTheme,
  defaultCssVariablesResolver,
  type MantineTheme,
  MantineColorsTuple,
} from '@mantine/core';
import { defaultMultiSelectRenderOption } from '../components/multiSelectRenderOption';

// Default size for Select, TextInput, Textarea, Button (and other inputs). Change here to update all at once.
// Mantine size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export const defaultInputSize = 'md';

// Gold-based color scheme: warm cream shell, sidebar #F9F8F2, active nav #EDE6D4, golden accent
// App shell: #F8F6F1, Sidebar: #F9F8F2, Active nav/badges: #EDE6D4, Primary accent: #C9A961
const gold: MantineColorsTuple = [
  '#F8F6F1', // 0 - App shell warm cream (lightest)
  '#EDE6D4', // 1 - Active nav link / badges / avatar (muted beige/gold)
  '#E8D7B0', // 2 - Light gold
  '#DFC898', // 3
  '#D6B97F', // 4
  '#C9A961', // 5 - Primary (golden/brownish accent)
  '#B89751', // 6
  '#A0895D', // 7 - Dark
  '#8A7450', // 8
  '#746143', // 9 - Darkest
];

// Muted clinical grays for backgrounds and borders
const clinicalGray: MantineColorsTuple = [
  '#FAFBFC', // 0 - Background
  '#F8F9FA', // 1 - Subtle background
  '#F3F4F6', // 2 - Hover states
  '#E5E7EB', // 3 - Disabled/subtle borders
  '#D1D5DB', // 4 - Light borders (unused, kept for scale)
  '#9CA3AF', // 5 - Primary borders & placeholder text
  '#6B7280', // 6 - Secondary text
  '#4B5563', // 7 - Tertiary text
  '#374151', // 8 - Body text
  '#1F2937', // 9 - Headings
];

// Softer red for clinical contexts (error state)
const softRed: MantineColorsTuple = [
  '#FEF2F2', // 0
  '#FEE2E2', // 1
  '#FECACA', // 2
  '#FCA5A5', // 3
  '#F87171', // 4
  '#EF4444', // 5 - Primary (softer, brighter red)
  '#DC2626', // 6 - Text color for light variants (lighter than before)
  '#B91C1C', // 7
  '#991B1B', // 8
  '#7F1D1D', // 9
];

// Muted green for success states
const clinicalGreen: MantineColorsTuple = [
  '#F0FDF4', // 0
  '#DCFCE7', // 1
  '#BBF7D0', // 2
  '#86EFAC', // 3
  '#4ADE80', // 4
  '#22C55E', // 5 - Primary
  '#16A34A', // 6
  '#15803D', // 7
  '#166534', // 8
  '#14532D', // 9
];

// Muted amber/yellow for warning states
const clinicalAmber: MantineColorsTuple = [
  '#FFFBEB', // 0
  '#FEF3C7', // 1
  '#FDE68A', // 2
  '#FCD34D', // 3
  '#FBBF24', // 4
  '#F59E0B', // 5 - Primary
  '#D97706', // 6
  '#B45309', // 7
  '#92400E', // 8
  '#78350F', // 9
];

// Muted blue for info states
const clinicalBlue: MantineColorsTuple = [
  '#EFF6FF', // 0
  '#DBEAFE', // 1
  '#BFDBFE', // 2
  '#93C5FD', // 3
  '#60A5FA', // 4
  '#3B82F6', // 5 - Primary
  '#2563EB', // 6
  '#1D4ED8', // 7
  '#1E40AF', // 8
  '#1E3A8A', // 9
];

export const mantineTheme = createTheme({
  primaryColor: 'gold',
  colors: {
    gold,
    unifyGold: gold,
    gray: clinicalGray,
    unifyGrey: clinicalGray,
    red: softRed,
    green: clinicalGreen,
    yellow: clinicalAmber,
    blue: clinicalBlue,
  },

  defaultRadius: 'md',

  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.3' },
      h2: { fontSize: '1.5rem', lineHeight: '1.3' },
      h3: { fontSize: '1.25rem', lineHeight: '1.4' },
      h4: { fontSize: '1.125rem', lineHeight: '1.4' },
      h5: { fontSize: '1rem', lineHeight: '1.4' },
      h6: { fontSize: '0.875rem', lineHeight: '1.4' },
    },
  },

  spacing: {
    xs: '0.5rem',    // 8px
    sm: '1rem',   // 16px
    md: '1.5rem',   // 24px - increased for more breathing room
    lg: '2rem',   // 32px - increased
    xl: '2.5rem',    // 40px - increased
  },

  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  },

  // Component-specific overrides for the muted clinical aesthetic
  components: {
    Paper: {
      defaultProps: {
        shadow: 'xs',
      },
      styles: {
        root: {
          borderColor: '#9CA3AF',
        },
      },
    },

    AppShell: {
      styles: {
        main: {
          backgroundColor: '#FDFBF7', // Warm off-white/cream – app shell
        },
        navbar: {
          backgroundColor: '#F9F6EF', // Sidebar – slightly different light cream shade
          borderRight: '1px solid #9CA3AF',
        },
        header: {
          backgroundColor: '#FFFFFF', // Same warm cream as shell
          borderBottom: '1px solid #9CA3AF',
        },
      },
    },

    NavLink: {
      defaultProps: {
        radius: 'md',
      },
      styles: (_theme: MantineTheme) => ({
        root: {
          borderRadius: '0.5rem',
          fontWeight: 500,
          '--nl-bg': 'var(--unify-nav-active)',
          '--nl-hover': 'var(--unify-nav-active)',
        },
      }),
    },

    Card: {
      defaultProps: {
        shadow: 'xs',
        radius: 'md',
        padding: 'lg',
      },
      styles: {
        root: {
          borderColor: '#9CA3AF',
        },
      },
    },

    Accordion: {
      defaultProps: {
        radius: 'md',
        variant: 'separated',
      },
      styles: (theme: MantineTheme) => ({
        item: {
          borderColor: '#9CA3AF',
          backgroundColor: '#FFFFFF',
        },
        control: {
          padding: theme.spacing.lg,
        },
        panel: {
          padding: theme.spacing.lg,
          paddingTop: 0,
          borderColor: '#9CA3AF',
        },
        label: {
          fontWeight: 600,
          fontSize: 'md',
          padding: 0
        },
        content: {
          padding: 0,
        },
        chevron: {
          color: '#6B7280',
        },
      }),
    },

    Button: {
      defaultProps: {
        radius: 'md',
        size: defaultInputSize,
      },
      styles: (_theme: MantineTheme, _props: Record<string, unknown>) => ({
        root: {
          fontWeight: 500,
        },
      }),
      vars: (theme: MantineTheme, props: Record<string, unknown>) => {
        if (props.variant === 'light') {
          const color = (props.color as keyof MantineTheme['colors']) || 'gold';
          return {
            root: {
              '--button-hover': theme.colors[color][1],
            },
          };
        }
        return { root: {} };
      },
    },



    TextInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    Textarea: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    NumberInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    PasswordInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    Autocomplete: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
        dropdown: {
          borderColor: '#9CA3AF',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    Select: {
      defaultProps: {
        size: defaultInputSize,
        allowDeselect: false,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
        dropdown: {
          borderColor: '#9CA3AF',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MultiSelect: {
      defaultProps: {
        size: defaultInputSize,
        withCheckIcon: false,
        renderOption: defaultMultiSelectRenderOption,
        searchable: true,
        clearSearchOnChange: true,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
        dropdown: {
          borderColor: '#9CA3AF',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    TagsInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    Combobox: {
      styles: {
        dropdown: {
          borderColor: '#9CA3AF',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
        search: {
          borderColor: '#9CA3AF',
        },
      },
    },

    InputBase: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    DateInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    DatePickerInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    TimeInput: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    DateTimePicker: {
      defaultProps: {
        size: defaultInputSize,
      },
      styles: {
        input: {
          borderColor: '#9CA3AF',
          '&:focus': {
            borderColor: '#C9A961',
          },
        },
      },
    },

    Popover: {
      styles: {
        dropdown: {
          borderColor: '#9CA3AF',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    Table: {
      defaultProps: {
        highlightOnHover: true,
      },
      styles: {
        table: {
          borderCollapse: 'collapse',
          border: 'none',
        },
        thead: {
          borderBottom: '1px solid #E5E7EB',
        },
        th: {
          fontWeight: 600,
          fontSize: '0.875rem', // match Text size="sm" as in StylingPage table headers
          lineHeight: 1.4,
          letterSpacing: '0.05em',
          color: '#6B7280',
          backgroundColor: 'transparent',
          border: 'none',
          borderBottom: '1px solid #E5E7EB',
          padding: '0.75rem 1rem',
          textTransform: 'none',
          verticalAlign: 'middle',
        },
        td: {
          border: 'none',
          borderBottom: '1px solid #E5E7EB',
          padding: '1rem 1rem',
          color: '#374151',
        },
      },
    },

    Menu: {
      styles: {
        dropdown: {
          borderColor: '#9CA3AF',
        },
      },
    },

    Tabs: {
      styles: {
        root: {
          '--tabs-list-border-size': '2px',
        },
        tab: {
          transition: 'all 0.2s ease',
        },
      },
    },

    Notification: {
      styles: {
        root: {
          borderColor: '#9CA3AF',
        },
      },
    },

    Modal: {
      styles: {
        content: {
          border: '1px solid #9CA3AF',
        },
      },
    },

    Drawer: {
      styles: {
        content: {
          border: '1px solid #9CA3AF',
        },
      },
    },

    Badge: {
      defaultProps: {
        color: 'gold',
        variant: 'light',
      },
      styles: {
        root: {
          fontWeight: 500,
        },
      },
    },

    Divider: {
      styles: {
        root: {
          borderColor: '#EFF1F3',
        },
      },
    },

    Checkbox: {
      defaultProps: {
        size: 'md',
        radius: 'md',
      },
      styles: {
        root: {
          '--label-offset-start': '0.5rem',
        },
      },
    },

    Pagination: {
      defaultProps: {
        size: 'md',
        withEdges: true,
      },
    },
  },
});

/** Theme object for MantineProvider (alias of mantineTheme) */
export const theme = mantineTheme;

/** CSS variables resolver for MantineProvider */
export const cssVariablesResolver = defaultCssVariablesResolver;
